import React from "react";
import { motion } from "framer-motion";

/**
 * Reusable component for scroll-reveal animations.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to animate
 * @param {'fade-up' | 'fade-in' | 'scale-up' | 'slide-left' | 'slide-right'} props.variants - Animation type
 * @param {number} props.delay - Animation delay in seconds
 * @param {number} props.duration - Animation duration in seconds
 * @param {number} props.distance - Slide distance
 * @param {number} props.threshold - Intersection threshold (0 to 1)
 * @param {string} props.className - Custom style classes
 */
const ScrollReveal = ({
  children,
  variants = "fade-up",
  delay = 0,
  duration = 1.2,
  distance = 50,
  threshold = 0.1,
  className = "",
}) => {
  const getVariants = () => {
    switch (variants) {
      case "fade-in":
        return {
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        };
      case "scale-up":
        return {
          hidden: { opacity: 0, scale: 0.8 },
          visible: { opacity: 1, scale: 1 },
        };
      case "slide-left":
        return {
          hidden: { opacity: 0, x: distance },
          visible: { opacity: 1, x: 0 },
        };
      case "slide-right":
        return {
          hidden: { opacity: 0, x: -distance },
          visible: { opacity: 1, x: 0 },
        };
      case "fade-up":
      default:
        return {
          hidden: { opacity: 0, y: distance },
          visible: { opacity: 1, y: 0 },
        };
    }
  };

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: threshold }}
      transition={{
        duration: duration,
        delay: delay,
        ease: [0.25, 0.1, 0.25, 1], // Smooth curve
      }}
      variants={getVariants()}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;
